import React, { useState, useRef } from 'react'
import { X, Shield, Camera, CheckCircle, AlertCircle, Upload, RefreshCw } from 'lucide-react'
import { Button } from '../ui/Button'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { useAuth } from '../../contexts/AuthContext'
import { activityLog } from '../../services/activityLog'
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock'
import toast from 'react-hot-toast'

interface KYCVerificationModalProps {
  isOpen: boolean
  onClose: () => void
  onComplete: () => void
}

export const KYCVerificationModal: React.FC<KYCVerificationModalProps> = ({
  isOpen,
  onClose,
  onComplete
}) => {
  const { verifyIdentity, isLoading, user } = useAuth()
  useBodyScrollLock(isOpen)
  const [step, setStep] = useState<'info' | 'document' | 'selfie' | 'confirm' | 'complete'>('info')
  const [documentType, setDocumentType] = useState<'drivers_license' | 'passport' | 'state_id'>('drivers_license')
  const [documentFront, setDocumentFront] = useState<File | null>(null)
  const [documentBack, setDocumentBack] = useState<File | null>(null)
  const [selfie, setSelfie] = useState<File | null>(null)
  const [isVerifying, setIsVerifying] = useState(false)
  const fileInputFrontRef = useRef<HTMLInputElement>(null)
  const fileInputBackRef = useRef<HTMLInputElement>(null)
  const fileInputSelfieRef = useRef<HTMLInputElement>(null)
  const [showCamera, setShowCamera] = useState(false)
  const [cameraMode, setCameraMode] = useState<'selfie' | 'document-front' | 'document-back' | null>(null)
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([])
  const [selectedCamera, setSelectedCamera] = useState<string>('')
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [videoReady, setVideoReady] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)


  React.useEffect(() => {
    return () => {
      stopCamera()
    }
  }, [])

  React.useEffect(() => {
    if (showCamera && stream && videoRef.current) {
      console.log('Setting video stream, tracks:', stream.getTracks().map(t => ({ kind: t.kind, enabled: t.enabled, readyState: t.readyState })))
      const video = videoRef.current
      video.srcObject = stream
      
      video.onloadedmetadata = () => {
        console.log('Video metadata loaded:', { width: video.videoWidth, height: video.videoHeight })
        setVideoReady(true)
        video.play().catch(err => {
          console.error('Error playing video:', err)
        })
      }
    }
  }, [showCamera, stream])

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop())
      setStream(null)
    }
    setShowCamera(false)
    setVideoReady(false)
    setCameraMode(null)
  }

  const startCamera = async (mode: 'selfie' | 'document-front' | 'document-back') => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Camera not supported in this browser. Please upload a photo instead.')
        return
      }
      const devices = await navigator.mediaDevices.enumerateDevices()
      const videoDevices = devices.filter(device => device.kind === 'videoinput')
      setCameras(videoDevices)
      
      if (videoDevices.length === 0) {
        toast.error('No camera detected. Please upload a photo instead.')
        return
      }
      if (videoDevices.length > 0) {
        const cameraId = selectedCamera || videoDevices[0].deviceId
        setSelectedCamera(cameraId)
        
        const facingMode = mode === 'selfie' ? 'user' : 'environment'
        
        const constraints: MediaStreamConstraints = {
          video: {
            deviceId: cameraId ? { exact: cameraId } : undefined,
            facingMode: cameraId ? undefined : facingMode,
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        }
        
        console.log('Requesting camera with constraints:', constraints)
        const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
        console.log('Got media stream:', mediaStream.getTracks())
        
        setCameraMode(mode)
        setStream(mediaStream)
        setShowCamera(true)
      }
    } catch (error: any) {
      console.error('Camera error:', error)
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Camera permission denied. Please allow camera access or upload a photo instead.')
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No camera found. Please upload a photo instead.')
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Camera is already in use. Please close other apps using the camera.')
      } else {
        toast.error('Unable to access camera. Please upload a photo instead.')
      }
    }
  }

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current && cameraMode) {
      const video = videoRef.current
      const canvas = canvasRef.current
      
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      
      const ctx = canvas.getContext('2d')
      if (ctx) {
        if (cameraMode === 'selfie') {
          ctx.translate(canvas.width, 0)
          ctx.scale(-1, 1)
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        
        canvas.toBlob((blob) => {
          if (blob) {
            const fileName = `${cameraMode}-${Date.now()}.jpg`
            const file = new File([blob], fileName, { type: 'image/jpeg' })
            
            if (cameraMode === 'selfie') {
              setSelfie(file)
            } else if (cameraMode === 'document-front') {
              setDocumentFront(file)
            } else if (cameraMode === 'document-back') {
              setDocumentBack(file)
            }
            
            stopCamera()
          }
        }, 'image/jpeg', 0.95)
      }
    }
  }

  const switchCamera = async () => {
    const currentIndex = cameras.findIndex(c => c.deviceId === selectedCamera)
    const nextIndex = (currentIndex + 1) % cameras.length
    const nextCamera = cameras[nextIndex].deviceId
    
    stopCamera()
    setSelectedCamera(nextCamera)
    
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: { exact: nextCamera },
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: false
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
      
      setStream(mediaStream)
      setShowCamera(true)
    } catch (error: any) {
      console.error('Switch camera error:', error)
      toast.error('Unable to switch camera. Please try again.')
      setShowCamera(false)
    }
  }

  const handleFileSelect = (file: File, type: 'front' | 'back' | 'selfie') => {
    if (type === 'front') {
      setDocumentFront(file)
    } else if (type === 'back') {
      setDocumentBack(file)
    } else if (type === 'selfie') {
      setSelfie(file)
    }
  }

  const handleStartVerification = () => {
    if (!documentFront || !selfie) {
      toast.error('Please upload all required documents')
      return
    }

    // Require back of ID for drivers_license and state_id
    if ((documentType === 'drivers_license' || documentType === 'state_id') && !documentBack) {
      toast.error('Please upload the back of your ID')
      return
    }

    setIsVerifying(true)
    verifyIdentity({
      documentType,
      documentFront,
      documentBack: documentBack || undefined,
      selfie
    })
      .then(() => {
        toast.success('Identity verified successfully!')
        const userName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : undefined
        activityLog.logIdentityVerification(userName)
        setTimeout(() => {
          onComplete()
          handleReset()
        }, 1500)
      })
      .catch((error) => {
        toast.error(error?.message || 'Verification failed. Please try again.')
      })
      .finally(() => {
        setIsVerifying(false)
      })
  }

  const handleReset = () => {
    setStep('info')
    setDocumentFront(null)
    setDocumentBack(null)
    setSelfie(null)
    stopCamera()
  }

  const handleClose = () => {
    if (!isVerifying) {
      handleReset()
      onClose()
    }
  }

  const handleStepClick = (targetStep: 'info' | 'document' | 'selfie' | 'confirm' | 'complete') => {
    if (isVerifying || targetStep === 'complete' || targetStep === 'confirm') {
      return
    }
    setStep(targetStep)
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay modal-overlay-nested">
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl dark:shadow-slate-900/50 border border-slate-200 dark:border-slate-700 p-6 max-w-2xl w-full mx-4 my-8 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-lg bg-[#69adba]/10 dark:bg-[#69adba]/20 flex items-center justify-center">
              <Shield className="h-5 w-5 text-[#69adba]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
                KYC/AML Verification
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Required to start your 1031 exchange
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isVerifying}
            className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <X className="h-5 w-5 text-slate-600 dark:text-slate-400" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200 dark:border-slate-700">
          {['info', 'document', 'selfie', 'confirm'].map((s, idx) => {
            const stepKey = s as typeof step
            const isActive = step === stepKey
            const stepOrder = ['info', 'document', 'selfie', 'confirm']
            const currentStepIndex = stepOrder.indexOf(step)
            const thisStepIndex = stepOrder.indexOf(stepKey)
            const isCompleted = thisStepIndex < currentStepIndex
            const isClickable = stepKey !== 'confirm' && !isVerifying
            
            return (
              <div key={s} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => handleStepClick(stepKey)}
                    disabled={!isClickable}
                    className={`w-8 h-8 rounded-full flex items-center justify-center border-2 transition-colors ${
                      isCompleted
                        ? 'bg-green-500 border-green-500 text-white'
                        : isActive
                        ? 'bg-[#69adba] border-[#69adba] text-white'
                        : 'bg-slate-100 dark:bg-slate-700 border-slate-300 dark:border-slate-600 text-slate-500 dark:text-slate-400'
                    } ${
                      isClickable ? 'cursor-pointer hover:border-[#69adba] hover:bg-[#69adba]/10 dark:hover:bg-[#69adba]/20' : 'cursor-default'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isCompleted ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <span className="text-xs font-semibold">{idx + 1}</span>
                    )}
                  </button>
                  <button
                    onClick={() => handleStepClick(stepKey)}
                    disabled={!isClickable}
                    className={`text-xs mt-1 text-center transition-colors whitespace-nowrap ${
                      isActive ? 'text-[#69adba] font-semibold' : 'text-slate-500 dark:text-slate-400'
                    } ${
                      isClickable ? 'cursor-pointer hover:text-[#69adba]' : 'cursor-default'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {s === 'info' ? 'Info' : s === 'document' ? 'ID' : s === 'selfie' ? 'Selfie' : 'Confirm'}
                  </button>
                </div>
                {idx < 3 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    isCompleted ? 'bg-green-500' : 'bg-slate-200 dark:bg-slate-700'
                  }`} />
                )}
              </div>
            )
          })}
        </div>

        {/* Step Content */}
        {step === 'info' && (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-1">
                    Why we need this
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300">
                    To comply with KYC (Know Your Customer) and AML (Anti-Money Laundering) regulations, 
                    we need to verify your identity before you can start a 1031 exchange. This process 
                    is secure and takes just a few minutes.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Document Type
              </label>
              <div className="grid grid-cols-3 gap-3">
                {(['drivers_license', 'passport', 'state_id'] as const).map((type) => (
                  <button
                    key={type}
                    onClick={() => setDocumentType(type)}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-medium transition-colors ${
                      documentType === type
                        ? 'border-[#69adba] bg-[#69adba]/10 dark:bg-[#69adba]/20 text-[#69adba]'
                        : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {type === 'drivers_license' ? 'Driver\'s License' : type === 'passport' ? 'Passport' : 'State ID'}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-4">
              <Button
                onClick={() => setStep('document')}
                className="w-full bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
              >
                Continue to Document Upload
              </Button>
            </div>
          </div>
        )}

        {step === 'document' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Upload Government ID
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Please upload clear photos or PDFs (PNG, JPG, PDF) of your {documentType === 'drivers_license' ? 'driver\'s license' : documentType === 'passport' ? 'passport' : 'state ID'}
              </p>
            </div>

            <div className="space-y-4">
              {!showCamera || cameraMode !== 'document-front' ? (
                <div>
                  <label htmlFor="document-front" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Front of ID {documentFront && <CheckCircle className="inline h-4 w-4 text-green-500 ml-1" />}
                  </label>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => startCamera('document-front')}
                      className="p-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-[#69adba] dark:hover:border-[#69adba] transition-colors flex flex-col items-center justify-center space-y-2"
                    >
                      <Camera className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Use Camera
                      </span>
                    </button>
                    
                    <input
                      id="document-front"
                      ref={fileInputFrontRef}
                      type="file"
                      accept="image/png,image/jpeg,image/jpg,application/pdf"
                      capture="environment"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileSelect(file, 'front')
                      }}
                      className="hidden"
                    />
                    <button
                      onClick={() => fileInputFrontRef.current?.click()}
                      className="p-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-[#69adba] dark:hover:border-[#69adba] transition-colors flex flex-col items-center justify-center space-y-2"
                    >
                      <Upload className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                      <span className="text-sm text-slate-600 dark:text-slate-400">
                        Upload Photo
                      </span>
                    </button>
                  </div>
                  
                  {documentFront && (
                    <div className="mt-2 space-y-1">
                      <div className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
                        <CheckCircle className="h-3 w-3" />
                        <span>Front captured successfully</span>
                      </div>
                      <div className={`relative mx-auto rounded-lg overflow-hidden border-2 border-green-200 dark:border-green-700 ${
                        documentType === 'passport' ? 'w-full max-w-[240px] aspect-[2/3]' : 'w-full max-h-32 aspect-[1.6/1]'
                      }`}>
                        <img
                          src={URL.createObjectURL(documentFront)}
                          alt="Document front preview"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Camera View - Front of ID
                  </label>
                <div className="relative bg-black rounded-lg overflow-hidden min-h-[320px] flex items-center justify-center">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`w-full min-h-[320px] object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`}
                  />
                  
                  {videoReady && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="relative w-[85%] max-w-[340px] aspect-[1.6/1]">
                        <div className="absolute inset-0 border-4 border-[#69adba] rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
                          <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-white rounded-tl-xl"></div>
                          <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-white rounded-tr-xl"></div>
                          <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-white rounded-bl-xl"></div>
                          <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-white rounded-br-xl"></div>
                        </div>
                        <div className="absolute -top-8 left-0 right-0 text-center">
                          <p className="text-white text-sm font-medium drop-shadow-lg bg-black/60 px-3 py-1 rounded-full inline-block">
                            Position ID within frame
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {!videoReady && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <LoadingSpinner size="lg" variant="icon" className="text-white" />
                      <span className="ml-2 text-white text-sm">Initializing camera...</span>
                    </div>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>
                <div className="mt-3 flex items-center justify-center gap-3">
                  {cameras.length > 1 && (
                    <button
                      onClick={switchCamera}
                      className="h-10 w-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-sm hover:shadow-md transition-all"
                    >
                      <RefreshCw className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                    </button>
                  )}
                  <button
                    onClick={capturePhoto}
                    className="h-12 w-12 flex items-center justify-center bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] rounded-full shadow-lg transition-all hover:scale-105"
                  >
                    <Camera className="h-6 w-6 text-white" />
                  </button>
                  <button
                    onClick={stopCamera}
                    className="h-10 w-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-sm hover:shadow-md transition-all"
                  >
                    <X className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-slate-600 dark:text-slate-400 text-center font-medium">
                    📱 Align your ID card within the frame
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-500 text-center">
                    Make sure all corners are visible and text is clear
                  </p>
                </div>
                </div>
              )}

              {(documentType === 'drivers_license' || documentType === 'state_id') && (
                !showCamera || cameraMode !== 'document-back' ? (
                  <div>
                    <label htmlFor="document-back" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Back of ID <span className="text-red-500">*</span> {documentBack && <CheckCircle className="inline h-4 w-4 text-green-500 ml-1" />}
                    </label>
                    
                    <div className="grid grid-cols-2 gap-3">
                      <button
                        onClick={() => startCamera('document-back')}
                        className="p-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-[#69adba] dark:hover:border-[#69adba] transition-colors flex flex-col items-center justify-center space-y-2"
                      >
                        <Camera className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          Use Camera
                        </span>
                      </button>
                      
                      <input
                        id="document-back"
                        ref={fileInputBackRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg,application/pdf"
                        capture="environment"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) handleFileSelect(file, 'back')
                        }}
                        className="hidden"
                      />
                      <button
                        onClick={() => fileInputBackRef.current?.click()}
                        className="p-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-[#69adba] dark:hover:border-[#69adba] transition-colors flex flex-col items-center justify-center space-y-2"
                      >
                        <Upload className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          Upload Photo
                        </span>
                      </button>
                    </div>
                    
                    {documentBack && (
                      <div className="mt-2 space-y-1">
                        <div className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
                          <CheckCircle className="h-3 w-3" />
                          <span>Back captured successfully</span>
                        </div>
                        <div className="relative w-full max-h-32 aspect-[1.6/1] mx-auto rounded-lg overflow-hidden border-2 border-green-200 dark:border-green-700">
                          <img
                            src={URL.createObjectURL(documentBack)}
                            alt="Document back preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                      Camera View - Back of ID
                    </label>
                    <div className="relative bg-black rounded-lg overflow-hidden min-h-[320px] flex items-center justify-center">
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className={`w-full min-h-[320px] object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`}
                      />
                      
                      {videoReady && (
                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                          <div className="relative w-[85%] max-w-[340px] aspect-[1.6/1]">
                            <div className="absolute inset-0 border-4 border-[#69adba] rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.5)]">
                              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-white rounded-tl-xl"></div>
                              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-white rounded-tr-xl"></div>
                              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-white rounded-bl-xl"></div>
                              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-white rounded-br-xl"></div>
                            </div>
                            <div className="absolute -top-8 left-0 right-0 text-center">
                              <p className="text-white text-sm font-medium drop-shadow-lg bg-black/60 px-3 py-1 rounded-full inline-block">
                                Position ID within frame
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {!videoReady && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <LoadingSpinner size="lg" variant="icon" className="text-white" />
                          <span className="ml-2 text-white text-sm">Initializing camera...</span>
                        </div>
                      )}
                      <canvas ref={canvasRef} className="hidden" />
                    </div>
                    <div className="mt-3 flex items-center justify-center gap-3">
                      {cameras.length > 1 && (
                        <button
                          onClick={switchCamera}
                          className="h-10 w-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-sm hover:shadow-md transition-all"
                        >
                          <RefreshCw className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                        </button>
                      )}
                      <button
                        onClick={capturePhoto}
                        className="h-12 w-12 flex items-center justify-center bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] rounded-full shadow-lg transition-all hover:scale-105"
                      >
                        <Camera className="h-6 w-6 text-white" />
                      </button>
                      <button
                        onClick={stopCamera}
                        className="h-10 w-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-sm hover:shadow-md transition-all"
                      >
                        <X className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                      </button>
                    </div>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-slate-600 dark:text-slate-400 text-center font-medium">
                        📱 Align the back of your ID within the frame
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-500 text-center">
                        Make sure all corners are visible and text is clear
                      </p>
                    </div>
                  </div>
                )
              )}

              <div className="flex gap-3 pt-4">
                <Button
                  variant="outline"
                  onClick={() => setStep('info')}
                  className="flex-1"
                >
                  Back
                </Button>
                <Button
                  onClick={() => setStep('selfie')}
                  disabled={!documentFront}
                  className="flex-1 bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
                >
                  Continue to Selfie
                </Button>
              </div>
            </div>
          </div>
        )}

        {step === 'selfie' && (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Take a Selfie
              </h3>
              <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                Please take a clear selfie or upload a photo (PNG, JPG, PDF) to verify your identity matches your ID
              </p>
            </div>

            {!showCamera || cameraMode !== 'selfie' ? (
              <div>
                <label htmlFor="selfie-photo" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Selfie {selfie && <CheckCircle className="inline h-4 w-4 text-green-500 ml-1" />}
                </label>
                
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => startCamera('selfie')}
                    className="p-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-[#69adba] dark:hover:border-[#69adba] transition-colors flex flex-col items-center justify-center space-y-2"
                  >
                    <Camera className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Use Camera
                    </span>
                  </button>
                  
                  <input
                    id="selfie-photo"
                    ref={fileInputSelfieRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    capture="user"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) handleFileSelect(file, 'selfie')
                    }}
                    className="hidden"
                  />
                  <button
                    onClick={() => fileInputSelfieRef.current?.click()}
                    className="p-6 border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg hover:border-[#69adba] dark:hover:border-[#69adba] transition-colors flex flex-col items-center justify-center space-y-2"
                  >
                    <Upload className="h-8 w-8 text-slate-400 dark:text-slate-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">
                      Upload Photo
                    </span>
                  </button>
                </div>
                
                {selfie && (
                  <div className="mt-2 space-y-1">
                    <div className="text-xs text-green-600 dark:text-green-400 flex items-center space-x-1">
                      <CheckCircle className="h-3 w-3" />
                      <span>Selfie captured successfully</span>
                    </div>
                    <div className="relative w-full max-w-[240px] aspect-[2/3] mx-auto rounded-lg overflow-hidden border-2 border-green-200 dark:border-green-700">
                      <img
                        src={URL.createObjectURL(selfie)}
                        alt="Selfie preview"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  Camera View - Selfie
                </label>
                <div className="relative bg-black rounded-lg overflow-hidden min-h-[400px] flex items-center justify-center">
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className={`w-full min-h-[400px] object-cover ${videoReady ? 'opacity-100' : 'opacity-0'}`}
                    style={{ transform: 'scaleX(-1)' }}
                  />
                  
                  {videoReady && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="relative">
                        <svg width="240" height="320" viewBox="0 0 240 320" className="drop-shadow-2xl">
                          <ellipse cx="120" cy="150" rx="92" ry="120" fill="none" stroke="#69adba" strokeWidth="4" strokeDasharray="8 4"/>
                          <ellipse cx="120" cy="150" rx="95" ry="123" fill="none" stroke="white" strokeWidth="2" opacity="0.6"/>
                        </svg>
                        
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-8">
                          <p className="text-white text-sm font-medium drop-shadow-lg bg-black/60 px-3 py-1 rounded-full whitespace-nowrap">
                            Position your face in the oval
                          </p>
                        </div>
                        
                      </div>
                    </div>
                  )}
                  
                  {!videoReady && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <LoadingSpinner size="lg" variant="icon" className="text-white" />
                      <span className="ml-2 text-white text-sm">Initializing camera...</span>
                    </div>
                  )}
                  <canvas ref={canvasRef} className="hidden" />
                </div>
                <div className="mt-3 flex items-center justify-center gap-3">
                  {cameras.length > 1 && (
                    <button
                      onClick={switchCamera}
                      className="h-10 w-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-sm hover:shadow-md transition-all"
                    >
                      <RefreshCw className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                    </button>
                  )}
                  <button
                    onClick={capturePhoto}
                    className="h-12 w-12 flex items-center justify-center bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] rounded-full shadow-lg transition-all hover:scale-105 ring-2 ring-white/40"
                  >
                    <Camera className="h-6 w-6 text-white" />
                  </button>
                  <button
                    onClick={stopCamera}
                    className="h-10 w-10 flex items-center justify-center bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-full shadow-sm hover:shadow-md transition-all"
                  >
                    <X className="h-5 w-5 text-slate-700 dark:text-slate-300" />
                  </button>
                </div>
                <div className="mt-2 space-y-1">
                  <p className="text-xs text-slate-600 dark:text-slate-400 text-center font-medium">
                    🤳 Center your face in the oval frame
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-500 text-center">
                    Make sure your entire face is visible and well-lit
                  </p>
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setStep('document')}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={() => setStep('confirm')}
                disabled={!selfie}
                className="flex-1 bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
              >
                Continue to Confirm
              </Button>
            </div>
          </div>
        )}

        {step === 'confirm' && (
          <div className="space-y-2">
            <div>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100 mb-1">
                Review and Confirm
              </h3>
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                Please review your information before submitting
              </p>
            </div>

            <div className="space-y-2">
              <div className="space-y-0.5">
                <div className="flex items-center space-x-2 p-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                  <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                    {documentType === 'passport' ? 'Passport' : documentType === 'drivers_license' ? 'Driver\'s License' : 'State ID'} - Front
                  </span>
                </div>
                {documentFront && (
                  <div className={`relative mx-auto rounded overflow-hidden border border-slate-200 dark:border-slate-700 ${
                    documentType === 'passport' ? 'w-full max-w-[140px] aspect-[2/3]' : 'w-full max-h-20 aspect-[1.6/1]'
                  }`}>
                    <img
                      src={URL.createObjectURL(documentFront)}
                      alt="Document front preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>

              {documentBack && (
                <div className="space-y-0.5">
                  <div className="flex items-center space-x-2 p-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                    <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                      {documentType === 'drivers_license' ? 'Driver\'s License' : 'State ID'} - Back
                    </span>
                  </div>
                  <div className="relative w-full max-h-20 aspect-[1.6/1] mx-auto rounded overflow-hidden border border-slate-200 dark:border-slate-700">
                    <img
                      src={URL.createObjectURL(documentBack)}
                      alt="Document back preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-0.5">
                <div className="flex items-center space-x-2 p-1.5 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded">
                  <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <span className="text-xs font-medium text-slate-700 dark:text-slate-300">Selfie Photo</span>
                </div>
                {selfie && (
                  <div className="relative w-full max-w-[140px] aspect-[2/3] mx-auto rounded overflow-hidden border border-slate-200 dark:border-slate-700">
                    <img
                      src={URL.createObjectURL(selfie)}
                      alt="Selfie preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-2">
              <div className="flex items-start space-x-2">
                <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  By clicking "Submit", you confirm that all information is accurate and the documents belong to you.
                </p>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setStep('selfie')}
                disabled={isVerifying}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={handleStartVerification}
                disabled={isVerifying}
                className="flex-1 bg-gradient-to-r from-[#69adba] to-[#5a9aa6] hover:from-[#5a9aa6] hover:to-[#69adba] text-white"
              >
                {isVerifying ? (
                  <>
                    <LoadingSpinner size="sm" variant="icon" className="mr-2" />
                    Verifying...
                  </>
                ) : (
                  'Submit Verification'
                )}
              </Button>
            </div>
          </div>
        )}

      </div>
    </div>
  )
}

